import { onMounted, onUnmounted, ref } from 'vue';

export interface CalendlyPrefill {
    name?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    customAnswers?: Record<string, string>;
}

export interface CalendlyUtm {
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmContent?: string;
    utmTerm?: string;
}

export interface CalendlyPopupOptions {
    url: string;
    prefill?: CalendlyPrefill;
    utm?: CalendlyUtm;
}

export interface CalendlyEventData {
    event: string;
    payload?: any;
}

export interface CalendlyMessageEvent extends MessageEvent {
    data: CalendlyEventData;
}

declare global {
    interface Window {
        Calendly?: {
            initPopupWidget: (options: CalendlyPopupOptions) => void;
            closePopupWidget: () => void;
        };
    }
}

export interface UseCalendlyOptions {
    onEventScheduled?: () => void;
    onProfileViewed?: () => void;
    onDateTimeSelected?: () => void;
    preload?: boolean;
}

let scriptAdded = false;
let scriptLoadingPromise: Promise<void> | null = null;

export function useCalendly(options: UseCalendlyOptions = {}) {
    const isLoading = ref(false);
    const isLoaded = ref(false);

    const ensureCalendlyLoaded = (): Promise<void> => {
        if (scriptLoadingPromise) {
            return scriptLoadingPromise;
        }

        if (window.Calendly) {
            isLoaded.value = true;
            return Promise.resolve();
        }

        const existingScript = document.querySelector<HTMLScriptElement>(
            'script[src="https://assets.calendly.com/assets/external/widget.js"]'
        );

        if (existingScript) {
            scriptLoadingPromise = new Promise((resolve) => {
                if (window.Calendly) {
                    isLoaded.value = true;
                    resolve();
                    scriptLoadingPromise = null;
                } else {
                    existingScript.addEventListener('load', () => {
                        isLoaded.value = true;
                        resolve();
                        scriptLoadingPromise = null;
                    });
                }
            });
            return scriptLoadingPromise;
        }

        scriptLoadingPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://assets.calendly.com/assets/external/widget.js';
            script.async = true;

            script.onload = () => {
                isLoaded.value = true;
                scriptAdded = true;
                resolve();
                scriptLoadingPromise = null;
            };

            script.onerror = () => {
                reject(new Error('Failed to load Calendly script'));
                scriptLoadingPromise = null;
            };

            document.body.appendChild(script);
        });

        return scriptLoadingPromise;
    };

    const openCalendly = async (popupOptions: CalendlyPopupOptions): Promise<void> => {
        try {
            isLoading.value = true;
            await ensureCalendlyLoaded();

            if (window.Calendly) {
                window.Calendly.initPopupWidget(popupOptions);
            } else {
                throw new Error('Calendly failed to initialize');
            }
        } catch (error) {
            console.error('Error opening Calendly:', error);
            throw error;
        } finally {
            isLoading.value = false;
        }
    };

    const closeCalendly = (): void => {
        if (window.Calendly) {
            window.Calendly.closePopupWidget();
        }
    };

    const handleCalendlyEvent = (e: CalendlyMessageEvent): void => {
        if (!e.data || typeof e.data !== 'object' || !e.data.event) {
            return;
        }

        switch (e.data.event) {
            case 'calendly.event_scheduled':
                options.onEventScheduled?.();
                break;

            case 'calendly.profile_page_viewed':
                options.onProfileViewed?.();
                break;

            case 'calendly.date_and_time_selected':
                options.onDateTimeSelected?.();
                break;
        }
    };

    onMounted(() => {
        window.addEventListener('message', handleCalendlyEvent as EventListener);

        if (options.preload) {
            ensureCalendlyLoaded();
        }
    });

    onUnmounted(() => {
        window.removeEventListener('message', handleCalendlyEvent as EventListener);
    });

    return {
        openCalendly,
        closeCalendly,
        isLoading,
        isLoaded,
    };
}